'use client'

import { useMemo, type ComponentProps } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import {
  ConversationMessageType,
  ConversationMessage,
  Message as MessageType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { useSession } from '@marketplace-web/shared/session-data'

import Message from '../../Message'
import PhotoMessages from './components/PhotoMessages'

type Props = ConversationMessage & {
  message: MessageType
  isGrouped: boolean
  isLast: boolean
} & Pick<ComponentProps<typeof PhotoMessages>, 'onThumbnailClick'>

const UserMessage = ({
  message,
  isGrouped,
  isLast,
  entityType,
  createdTime,
  onThumbnailClick,
}: Props) => {
  const { conversation } = useConversationContext()
  const isReadByOppositeUser = conversation?.isReadByOppositeUser ?? false

  const isMessageBubbleVisible = message.content || message.isHidden

  const { user } = useSession()

  const currentUserId = user?.id
  const oppositeUser = conversation?.oppositeUser

  const isCurrentAuthor = useMemo(() => {
    const isPeekingUser =
      message.userId !== currentUserId?.toString() && oppositeUser?.id !== String(message?.userId)

    if (isPeekingUser) {
      return String(message.userId) !== oppositeUser?.id
    }

    return message.userId === currentUserId?.toString()
  }, [message, currentUserId, oppositeUser])

  return (
    <>
      {isMessageBubbleVisible && (
        <Message
          createdTime={createdTime}
          isCurrentAuthor={isCurrentAuthor}
          isGrouped={isGrouped}
          isLast={isLast}
          isLastAndRead={isLast && isReadByOppositeUser}
          isPortalMessage={entityType === ConversationMessageType.PortalMessage}
          message={message}
        />
      )}
      <PhotoMessages
        createdTime={createdTime}
        isCurrentAuthor={isCurrentAuthor}
        isLast={isLast}
        message={message}
        isReadByOppositeUser={isReadByOppositeUser}
        onThumbnailClick={onThumbnailClick}
      />
    </>
  )
}

export default UserMessage
